import { noteMapping, noteMappingHashMap } from '../../constants/moldOrder';
import { formatDate, isStoreOrderType, isAllStoreOrProductionType, getShiftLabel, isTestingOrderType } from '../../utils/dateUtils';
import {
  ButtonOptionsFieldConfig,
  FormatterMap,
  FormFieldConfig,
  MoldOrderType, moldOrderTypesForMaintenance,
  moldOrderTypesForRepair,
  SearchDropdownFieldConfig,
  SelectFieldConfig,
  SelectWithOtherFieldConfig
} from '../../types/mold';
import { locationStorageService, injectionMachineService } from '../../services/api';
import { MoonIcon, SunIcon } from 'lucide-react';
import fetchersRegistry from '../Questionnaire/fetchersRegistry';

export const formFieldsForMoldAcceptance: FormFieldConfig[] = [
  {
    key: 'expectedArrivalDate',
    label: 'วันเข้าโรงงาน (คาดการณ์)',
    type: 'date',
    required: true,
  },
]

export const formFieldsForRepair: FormFieldConfig[] = [
  {
    key: 'moldOrderType',
    label: 'ประเภทการดำเนินการ',
    type: 'select',
    options: moldOrderTypesForRepair,
    required: true,
  },
  {
    key: 'problemDuring',
    label: 'ปัญหาเกิดระหว่าง',
    type: 'select',
    options: [],
    fetch: fetchersRegistry['repairProblemDuringList'],
    required: true,
    showIf: (details) => {
      return [
        MoldOrderType.REPAIR_MAJOR,
        MoldOrderType.REPAIR_MINOR,
      ].includes(details.moldOrderType)
    },
  },
  {
    key: 'machineToProduce',
    type: 'search_dropdown',
    label: 'เครื่องที่ทำการผลิต (ถ้ามี)',
    placeholder: 'ระบุเครื่องที่ทำการผลิต',
    options: [],
    fetch: injectionMachineService.getOptions,
    required: false,
    enforceDropdownSelection: true,
    validationMessage: "กรุณาเลือกตัวเลือกที่ถูกต้องจากรายการ",
    allowInvalidInput: false,
    showIf: (details) => {
      return [
        MoldOrderType.REPAIR_MAJOR,
        MoldOrderType.REPAIR_MINOR,
      ].includes(details.moldOrderType)
    },
  },
  {
    key: 'reporterName',
    label: 'ชื่อผู้แจ้งปัญหา (ถ้ามี)',
    type: 'text',
    placeholder: 'ระบุชื่อผู้แจ้งปัญหา',
    required: false,
    showIf: (details) => {
      return [
        MoldOrderType.REPAIR_MAJOR,
        MoldOrderType.REPAIR_MINOR,
      ].includes(details.moldOrderType)
    },
  },
  {
    key: 'dueDate',
    label: 'กำหนดเสร็จ',
    type: 'date',
    required: true,
    defaultDateOffset: 3,
  },
  {
    key: 'modifyRequirementNote',
    label: 'รายละเอียดที่ต้องการให้ดำเนินการ',
    type: 'textarea',
    placeholder: 'ระบุรายละเอียดที่ต้องการให้ดำเนินการ',
    required: true,
    showIf: (details) => {
      return [
        MoldOrderType.MODIFY,
      ].includes(details.moldOrderType)
    },
  }
]

export const formFields: FormFieldConfig[] = [
  {
    key: 'moldOrderType',
    label: 'ประเภทการล้าง',
    type: 'select',
    options: moldOrderTypesForMaintenance,
    required: true,
  },
  {
    key: 'machineToProduce',
    type: 'search_dropdown',
    label: 'เครื่องที่ผลิต',
    placeholder: 'ระบุเครื่องที่ผลิต',
    options: [],
    fetch: injectionMachineService.getOptions,
    required: (details) => {
      return [
        MoldOrderType.ALL_PRODUCTION,
        MoldOrderType.WAITING,
      ].includes(details.moldOrderType)
    },
    enforceDropdownSelection: true,
    validationMessage: "กรุณาเลือกตัวเลือกที่ถูกต้องจากรายการ",
    allowInvalidInput: false
  },
  {
    key: 'storageLocation',
    label: 'สถานที่เก็บหลังล้าง',
    type: 'select_with_other',
    options: [],
    fetch: locationStorageService.getAll,
    placeholder: 'ระบุสถานที่เก็บ',
    displayRawValue: true,
    showIf: (details) => details.moldOrderType && isStoreOrderType(details.moldOrderType),
    required: true,
    defaultValueFromMold: (mold) => mold.storageLocation,
  },
  {
    key: 'customStorageLocation',
    label: 'ระบุสถานที่เก็บเพิ่มเติม',
    type: 'text',
    placeholder: 'สถานที่เก็บ',
    showIf: (details) => details.storageLocation === 'other_custom_text',
    required: false,
  },
  {
    key: 'accumulativeShotCount',
    label: 'จำนวน SHOT สะสมที่ล้างครั้งนี้',
    placeholder: 'ระบุจำนวน SHOT',
    type: 'number',
    showIf: (details) => details.moldOrderType && isAllStoreOrProductionType(details.moldOrderType),
    required: true,
    defaultValueFromMold: (mold) => mold.cycleShotCount,
  },
  {
    key: 'dueDate',
    label: 'กำหนดเสร็จ',
    type: 'date',
    required: true,
    defaultDateOffset: 3,
  },
  {
    key: 'shiftCompleted',
    label: 'กะที่จะผลิต',
    type: 'button_options',
    options: [
      {
        value: 'A',
        label: getShiftLabel('A'),
        icon: SunIcon,
        borderColor: 'border-yellow-500',
        textColor: 'text-yellow-500',
        hoverColor: 'hover:bg-yellow-500',
        bgColor: 'bg-white',
        selectedBgColor: 'bg-yellow-500',
      },
      {
        value: 'B',
        label: getShiftLabel('B'),
        icon: MoonIcon,
        borderColor: 'border-blue-500',
        textColor: 'text-blue-500',
        hoverColor: 'hover:bg-blue-500',
        bgColor: 'bg-white',
        selectedBgColor: 'bg-blue-500',
      },
    ],
    placeholder: 'ระบุกะที่จะผลิต',
    showIf: (details) => {
      if ([MoldOrderType.MAINTENANCE_TESTING].includes(details.moldOrderType)) {
        return true;
      }
      return details.moldOrderType && !isStoreOrderType(details.moldOrderType) && !isTestingOrderType(details.moldOrderType);
    },
    required: true,
  },
  {
    key: 'note',
    label: 'คำสั่งเพิ่มเติม',
    type: 'select',
    options: noteMapping.map((value: string) => {
      if (value === 'other_custom_text') {
        return { value, label: noteMappingHashMap[value] };
      }
      return { value, label: value }
    }),
    required: true,
  },
  {
    key: 'customNote',
    label: 'ระบุคำขอให้ดำเนินการเพิ่มเติม',
    type: 'text',
    placeholder: 'ระบุข้อความพิมพ์',
    showIf: (details) => details.note === 'other_custom_text',
    required: true,
  },
];

export const formatFieldValue = (
  field: FormFieldConfig,
  value: any,
): string | undefined => {
  const formatter = formatters[field.type];
  if (!formatter) return value;

  if (field.type === 'select') {
    return formatter.format(value, (field as SelectFieldConfig).options);
  }

  if (field.type === 'button_options') {
    return formatter.format(value, (field as ButtonOptionsFieldConfig).options);
  }

  if (field.type === 'select_with_other') {
    if (field.displayRawValue) {
      return value === 'other_custom_text' ? 'อื่นๆ' : value;
    } else {
      const displayOptions = (field as SelectWithOtherFieldConfig).options;
      return formatter.format(value, displayOptions.concat([{ value: 'other_custom_text', label: 'อื่นๆ' }]));
    }
  }

  if (field.type === 'search_dropdown') {
    return formatter.format(value, (field as SearchDropdownFieldConfig).options);
  }

  const formattedValue = formatter.format(value);

  if (field.type === 'number' && formattedValue) {
    return `${formattedValue} SHOT`;
  }

  return formattedValue;
};

export const validateField = (field: FormFieldConfig, value: any): boolean => {
  if (!field.required && !value) return true;

  const validator = formatters[field.type]?.validate;
  if (!validator) return true;

  return validator(value);
};

const formatters: FormatterMap = {
  button_options: {
    format: (value: string, options?: Array<{ value: string; label: string }>) => {
      const option = options?.find((opt) => opt.value === value);
      return option?.label;
    },
    validate: (value: string) => Boolean(value),
  },
  select: {
    format: (
      value: string,
      options?: Array<{ value: string; label: string }>,
    ) => {
      const option = options?.find((opt) => opt.value === value);
      return option?.label ?? value;
    },
    validate: (value: string) => Boolean(value),
  },
  select_with_other: {
    format: (
      value: string,
      options?: Array<{ value: string; label: string }>,
    ) => {
      const option = options?.find((opt) => opt.value === value);
      return option?.label ?? value;
    },
    validate: (value: string) => Boolean(value),
  },
  select_with_other_and_custom: {
    format: (value: string) => value || undefined,
    validate: (value: string) => Boolean(value),
  },
  date: {
    format: (value: Date) => formatDate(value),
    validate: (value: Date) => Boolean(value),
  },
  text: {
    format: (value: string) => value || undefined,
    validate: (value: string) => Boolean(value?.trim()),
  },
  textarea: {
    format: (value: string) => value || undefined,
    validate: (value: string) => Boolean(value?.trim()),
  },
  number: {
    format: (value: number) => value?.toString() || undefined,
    validate: (value: number) => !isNaN(value) && value > 0,
  },
  boolean: {
    format: (value: boolean) => (value ? 'Yes' : 'No'),
    validate: (value: boolean) => typeof value === 'boolean',
  },
  toggle: {
    format: (value: boolean) => (value ? 'Yes' : 'No'),
    validate: (value: boolean) => typeof value === 'boolean',
  },
  multi_selector: {
    format: (value: string[]) => value?.join(', ') || undefined,
    validate: (value: string[]) => Array.isArray(value) && value.length > 0,
  },
  computed: {
    format: (value: unknown) => value == null ? undefined : String(value),
  },
  search_dropdown: {
    format: (
      value: string,
      options?: Array<{ value: string; label: string }>,
    ) => {
      return value;
    },
    validate: (value: string, options?: Array<{ value: string; label: string }>) => {
      if (!value) return false;
      return options ? options.some((opt) => String(opt.value) === String(value)) : false;
    },
  },
};
